import React, { useEffect, useState, useContext } from "react";
import { ThemeContext } from "../contexts/ThemeContext";

/**
 * Fade In / Out child components
 * @param {React.JSXElementConstructor<any>} props 
 * @returns 
 */
export default function FadeIn(props) {

    const { reducedMotion } = useContext(ThemeContext);

    const { stopMovement } = props;
    // Always respect reducedMotion. 
    // If reducedMotion is false, and stopMovement is not set (or false), 
    // then animate as normal. If reducedMotion is false and 
    // stopMovement is true, then don't do the X or Y 
    // animation. If reducedMotion is true, then don't 
    // do the X or Y animation.
    const doAnimation = !reducedMotion && (!stopMovement || stopMovement === false);
    
    const [maxIsVisible, setMaxIsVisible] = useState(0);
    const transitionDuration =
        typeof props.transitionDuration === "number"
            ? props.transitionDuration
            : 400;
    const delay = typeof props.delay === "number" ? props.delay : 50;
    const WrapperTag = props.wrapperTag || "div";
    const ChildTag = props.childTag || "div";
    const visible =
        typeof props.visible === "undefined" ? true : props.visible;
    const direction = props.direction || "up";
    const distance = props.distance || "20px";

    // Compile the animation style
    const animationStyle = direction === "up" ? "translateY" : "translateX";

    useEffect(() => {
        let count = React.Children.count(props.children);
        if (!visible) {
            // Animate all children out
            count = 0;
        }

        if (count === maxIsVisible) {
            // We're done updating maxVisible, notify when animation is done
            const timeout = setTimeout(() => {
                if (props.onComplete) props.onComplete();
            }, transitionDuration);
            return () => clearTimeout(timeout);
        }

        // Move maxIsVisible toward count
        const increment = count > maxIsVisible ? 1 : -1;
        const timeout = setTimeout(() => {
            setMaxIsVisible(maxIsVisible + increment);
        }, delay);
        return () => clearTimeout(timeout);
    }, [
        React.Children.count(props.children),
        delay,
        maxIsVisible,
        visible,
        transitionDuration,
    ]);

    let styleProps = {
        transition: `opacity ${transitionDuration}ms, transform ${transitionDuration}ms`,
        flexGrow: 1,
        
    }

    if (props.removeFlexGrow) {
        delete styleProps.flexGrow;
    }

    return (
        <WrapperTag className={props.className}>
            {React.Children.map(props.children, (child, i) => (
                <ChildTag
                    className={props.childClassName}
                    style={{
                        ...styleProps, 
                        transform: doAnimation ? maxIsVisible > i ? "none" : `${animationStyle}(${distance})` : "none",
                        opacity: maxIsVisible > i ? 1 : 0,
                        height: child.childHeightFitParent ? "100%" : "auto"
                    }}
                    id="fade-in-child"
                >
                    {child}
                </ChildTag>
            ))}
        </WrapperTag>
    );
}
